import { reactive, ref } from "vue"
import { DISTRIBUTION } from "@/config/distribution"

export const SchultenMedia = reactive({
    APP_URL: import.meta.env.VITE_APP_URL, // local laravel when testing on android emulator
    API_URL: import.meta.env.VITE_API_URL,
    API_VERSION: import.meta.env.VITE_API_VERSION || 'v1',
    DISTRIBUTION_ID: DISTRIBUTION.id,
    // Deliberately retained in production bundles so the release guard can
    // prove that Capacitor copied the selected client build, not the previous
    // client from a batch release.
    DISTRIBUTION_MARKER: DISTRIBUTION.marker,
    // Pusher Configuration
    PUSHER_APP_ID: import.meta.env.VITE_PUSHER_APP_ID,
    PUSHER_KEY: import.meta.env.VITE_PUSHER_KEY,
    PUSHER_CLUSTER: import.meta.env.VITE_PUSHER_CLUSTER,
    Bearer: "",
    Authenticated: false,
    User: null as any,
    Workouts: ref(),
    CurrentWorkout: ref(),
    API: {
        auth: {
            LOGIN: "/auth/login",
            REGISTER: "/auth/register",
            FORGOT_PASSWORD: "/auth/forgot-password",
            VERIFY_OTP: "/auth/verify-otp",
            RESET_PASSWORD: "/auth/reset-password",
            VERIFY_TOKEN: "/auth/verify-token",
            IMPERSONATE_EXCHANGE: "/auth/impersonate/exchange",
            CONNECT: "/auth/connect/:name",
            DISCONNECT: "/auth/disconnect/:name"
        },
        dashboard: {
            PLAN: "/dashboard/plan",
            GOAL: "/dashboard/goal",
            HABIT: "/dashboard/habit",
            PROGRESS: "/dashboard/progress",
            CHECKINS: "/dashboard/check-ins",
            WEEKLY_SUMMARY_DOWNLOAD: "/dashboard/weekly-summary/download",
            ANNOUNCEMENT: "/dashboard/announcement",
            ANNOUNCEMENT_DISMISS: "/dashboard/announcement/:uuid/dismiss",
        },
        submission_form: {
            FETCH: "/form/get/:uuid",
            SUBMIT: "/form/submit",
            DRAFT: "/form/draft",
            getCheckins: "/form/checkins",
            compareCheckins: "/form-submissions/compare",
            getForms: "/user-forms"
        },
        userProfile: {
            update: "user-profile/update",
            changePassword: "user-profile/change-password",
            delete: "user-profile/delete",
            changeNotification: "user-profile/notification",
            savePreferences: "user-profile/preferences",
            metrics: "user-profile/metrics",
            metricsLog: "user-profile/metrics/:user_metric_id/log",
            progressPhotos: "user-profile/progress-photos",
            progressPhotoDetail: "user-profile/progress-photos/:uuid",
            notes: "user-profile/notes",
            appleHealth: {
                oauth: "fitness-providers/apple_health/oauth",
                webhook: "fitness-providers/apple_health/webhook",
            },
            healthConnect: {
                oauth: "fitness-providers/health_connect/oauth",
                webhook: "fitness-providers/health_connect/webhook",
            },
            fitnessProviders: {
                fields: "fitness-providers/:provider/fields",
                mappings: "fitness-providers/:provider/mappings",
            },
        },
        calendar: {
            events: {
                index: "calendar/events",
                create: "calendar/events",
                update: "calendar/events/:id",
                delete: "calendar/events/:id",
                invitation: "calendar/events/invitation/:id",
                accept: "calendar/events/invitation/:id/accept",
                decline: "calendar/events/invitation/:id/decline",
            },
            categories: {
                index: "calendar/categories",
                create: "calendar/categories",
                delete: "calendar/categories/:id",
            },
            google: {
                index: "calendar/google",
                save: "calendar/google",
            }
        },
        nutrition: {
            get: "nutritions",
            deleteFood: "nutritions/:uuid/meal/food",
            copyMeal: "nutritions/:uuid/meals",
            updateFood: "nutritions/:uuid/meal/food",
            updateMeal: "nutritions/:uuid",
            clearMeal: "nutritions/:uuid/meals/:meal_uuid/clear",
            deleteMeal: "nutritions/:uuid",
            updateWaterIntake: "nutritions/:uuid/water-intake",
            searchFood: "foods",
            searchAllergies: "nutritions/allergies",
            createFood: "foods",
            // The client's own foods (COACHIO-1405). Not `updateFood` above: that
            // one changes the portion of a logged row.
            customFoods: {
                user: "foods/user",
                show: "foods/:uuid",
                update: "foods/:uuid",
                delete: "foods/:uuid",
            },
            searchRecipes: "recipes",
            addCustomFood: "nutritions/:uuid",
            addRecipe: "nutritions/:uuid/recipe",
            report: "nutrition/report",
            reorderMeal: "nutrition/meal/:uuid/reorder",
            suggestions: {
                // date: "nutrition/suggestions/:date",
                alternative: "nutrition/suggestions/alternative",
            },
            grocery: {
                get: "nutrition/grocery-list",
                save: "nutrition/grocery-list",
                delete: "nutrition/grocery-list",
                prepare: "nutrition/grocery-list/prepare",
            },
            supplements: {
                add: "nutritions/:uuid/supplements",
                delete: "nutritions/:uuid/supplements",
                update: "nutritions/:uuid/supplements",
            },
            meal: {
                create: "nutritions",
            },
            recipes: {
                user: "recipes/user",
                filters: "recipes/filters",
                filterCounts: "recipes/filters/counts",
                create: "recipes",
                update: "recipes/:uuid",
                delete: "recipes/:uuid",

                copy: "recipes/:uuid/copy",
                saveMeal: "nutritions/:uuid/meals/:meal_uuid/save-as-recipe",
            },
        },
        workout: {
            get: "workouts",
            getByUuid: "workouts/:uuid",
            complete: "workouts/:uuid/complete", // uuid is workout uuid
            skip: "workouts/:uuid/skip", // uuid is workout uuid
            saveLog: "workouts/save",
            history: "workouts/:uuid/history", // uuid is excercise uuid
            report: "workouts/report",
            program: "workouts/programs/:uuid", // uuid is program uuid - get full cardio program
            exercise: {
                updateNote: 'workouts/:uuid/note/update', // uuid is user excercise uuid
                get: 'workouts/:uuid/exercise/:exerciseUuid', // uuid is workout uuid and exerciseUuid is user excercise uuid
                find: 'workouts/:uuid/find/exercises', // uuid is user excercise uuid
                replace: 'workouts/:uuid/replace/exercise' // uuid is user excercise uuid
            }
        },
        habits: {
            template: "habits/templates",
            single: "habit/:uuid",
            get: "habits",
            add: "habits",
            update: "habits/:uuid",
            delete: "habits/:uuid",
            complete: "habits/:uuid/complete",
            log: "habits/:uuid/log/:date",
            report: "habits/report",
        },
        forms: {
            report: "forms/report",
        },
        terms: {
            get: "terms",
            accept: "terms/accept",
        },
        tenant: {
            features: "tenant/features",
        },
        chats: {
            getChats: "chats/get",
            chatRead: "chats/read",
            sendMessage: "chats/send-message",
            deleteMessage: "chats/message/:id",
            downloadAttachment: "chats/attachment/:uuid",
            typing: "chats/typing",
            leave: "chats/leave",
            getMessages: "chats/get/messages/:uuid"
        },
        booking: {
            timetable: "booking/timetable",
            reservations: "booking/reservations",
            bookSession: "booking/sessions/:uuid/book",
            cancelSession: "booking/sessions/:uuid/book",
        },
        credits: {
            balance: "credits/balance",
            mutations: "credits/mutations",
        },
        products: {
            creditPacks: "products/credit-pack",
            purchase: "products/:uuid/purchase",
        },
        orders: {
            list: "orders",
            detail: "orders/:uuid",
        },
        notification: {
            saveToken: "save-token",
        },
        cycle: {
            current: "cycle/current",
            summaries: "cycle/summaries",
            phases: "cycle/phases",
            events: "cycle/events",
            settings: "cycle/settings",
            consent: "cycle/consent",
            update: "cycle/:uuid",
            pregnancy: {
                current: "pregnancy/current",
                store: "pregnancy",
                end: "pregnancy/current/end",
                vitals: "pregnancy/vitals",
                consent: "pregnancy/consent",
            },
        },
        availability: {
            list: "availability",
            item: "availability/:uuid",
        },
        TMP_UPLOAD_MEDIA: "upload-media-file",
        GET_WORKOUTS: "exercises/get",
        SAVE_WORKOUT_DATA: "exercises/save",
        GET_EXERCISE_HISTORY: "exercises/history/get",
        FINISH_WORKOUT: "exercises/finish",
    },
})
